import {
    ContextMenuProvider,
    ImageEditOperation,
    ExperimentalFeatures,
} from 'roosterjs-editor-types';
import {
    ImageEdit,
    canRegenerateImage,
    isResizedTo,
    resizeByPercentage,
    resetImage,
} from 'roosterjs-editor-plugins/lib/ImageEdit';
import { safeInstanceOf } from 'roosterjs-editor-dom';
import {
    imageSizeBestFit,
    imageSizeSmall,
    imageSizeMedium,
    imageSizeOriginal,
    imageContextMenuItemSize,
    imageContextMenuRemove,
    imageContextMenuCrop,
} from './OwaImageEdit.locstring.json';
import loc from 'owa-localize';
import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';

const enum ImageResizeState {
    Small = 'small',
    Medium = 'medium',
    BestFit = 'bestFit',
    Original = 'original',
    Other = 'other',
}

const ResizePercentage = {
    [ImageResizeState.Small]: 0.25,
    [ImageResizeState.Medium]: 0.5,
    [ImageResizeState.BestFit]: 0,
    [ImageResizeState.Original]: 1,
};

export default class OwaImageEdit
    extends ImageEdit
    implements ContextMenuProvider<IContextualMenuItem> {
    public getContextMenuItems(image: Node) {
        if (safeInstanceOf(image, 'HTMLImageElement') && image.isContentEditable) {
            const canCrop =
                this.editor.isFeatureEnabled(ExperimentalFeatures.ImageCrop) &&
                canRegenerateImage(image);
            const menuNameMap = {
                [ImageResizeState.BestFit]: loc(imageSizeBestFit),
                [ImageResizeState.Small]: loc(imageSizeSmall),
                [ImageResizeState.Medium]: loc(imageSizeMedium),
                [ImageResizeState.Original]: loc(imageSizeOriginal),
            };

            return [
                {
                    name: loc(imageContextMenuItemSize),
                    key: 'resize',
                    subMenuProps: {
                        items: Object.keys(menuNameMap).map(name => ({
                            name: menuNameMap[name],
                            key: name,
                            onClick: (ev, item) => this.setImageSize(image, item.key),
                            isChecked: isResizedTo(image, ResizePercentage[name]),
                            canCheck: true,
                        })),
                    },
                },
                canCrop
                    ? {
                          name: loc(imageContextMenuCrop),
                          key: 'crop',
                          onClick: () => this.setEditingImage(image, ImageEditOperation.Crop),
                      }
                    : null,
                {
                    name: loc(imageContextMenuRemove),
                    key: 'remove',
                    onClick: () => {
                        if (this.editor?.contains(image)) {
                            this.editor.addUndoSnapshot(() => {
                                this.editor.deleteNode(image);
                                this.setEditingImage(null);
                            }, 'DeleteImage');
                        }
                    },
                },
            ].filter(x => !!x) as IContextualMenuItem[];
        } else {
            return null;
        }
    }

    private setImageSize(image: HTMLImageElement, imageSize: string) {
        if (this.editor?.contains(image)) {
            this.editor.addUndoSnapshot(() => {
                switch (imageSize) {
                    case ImageResizeState.Small:
                    case ImageResizeState.Medium:
                    case ImageResizeState.Original:
                        resizeByPercentage(
                            this.editor,
                            image,
                            ResizePercentage[imageSize],
                            this.options.minWidth!,
                            this.options.minHeight!
                        );
                        break;
                    case ImageResizeState.BestFit:
                        resetImage(this.editor, image);
                        break;
                }
            });
        }
    }
}
